/**
 * UnusualLoginsStrip — real-time amber alert above the Admin
 * Dashboard status row whenever any user has signed in from a
 * country that differs from their modal (most-common-historical)
 * sign-in country in the last 24h.
 *
 * Backed by GET /api/security/unusual-logins-recent. Self-hides
 * when there's nothing to triage. Per-row "Inspect" button opens
 * the user's detail slide-over so the admin can pivot directly
 * from anomaly → investigation → force-sign-out without ever
 * leaving the page.
 *
 * Polls every 60s. Pauses while the tab is hidden so it doesn't
 * waste API calls in backgrounded windows.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { AlertTriangle, Globe, Clock, ChevronRight } from 'lucide-react';
import api from '../../lib/api';

const POLL_INTERVAL_MS = 60_000;

const flagEmoji = (code) => {
  if (!code || code.length !== 2) return '';
  try {
    return String.fromCodePoint(
      ...[...code.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65),
    );
  } catch {
    return '';
  }
};

const relTime = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const diff = Date.now() - t;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(iso).toLocaleDateString();
};

export default function UnusualLoginsStrip({ onInspectUser }) {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const intervalRef = useRef(null);

  const fetchRows = useCallback(async () => {
    try {
      const res = await api.get('/security/unusual-logins-recent?window_hours=24');
      setRows(res?.data?.rows || []);
    } catch {
      // Read-only telemetry — silently degrade rather than scaring
      // the admin with an error toast for a transient blip.
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchRows();
    const onVisibility = () => {
      if (!document.hidden) fetchRows();
    };
    document.addEventListener('visibilitychange', onVisibility);
    intervalRef.current = setInterval(() => {
      if (!document.hidden) fetchRows();
    }, POLL_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchRows]);

  // Self-hide when nothing to triage — that's the win condition.
  if (!loaded || rows.length === 0) return null;

  return (
    <Card
      className="mb-4 border-amber-300 bg-amber-50/60 ring-1 ring-amber-200"
      data-testid="unusual-logins-strip"
    >
      <CardContent className="py-3 px-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-amber-600" />
            <span className="text-sm font-semibold text-amber-900">
              Unusual sign-in location{rows.length === 1 ? '' : 's'}
            </span>
            <span className="text-xs px-1.5 py-0.5 bg-amber-200 text-amber-900 rounded font-mono"
              data-testid="unusual-logins-count">
              {rows.length}
            </span>
          </div>
          <span className="text-[11px] text-amber-700/80">
            <Clock size={10} className="inline mr-1" />
            last 24h · refreshes every 60s
          </span>
        </div>
        <p className="text-xs text-amber-800/80 mb-2">
          {rows.length === 1
            ? 'A user signed in from a country that differs from their usual sign-in location.'
            : `${rows.length} users signed in from countries that differ from their usual sign-in locations.`}
          {' '}Inspect each session below — force-sign-out is one click away inside the user detail slide-over.
        </p>
        <div className="space-y-1.5">
          {rows.slice(0, 5).map((row) => (
            <div
              key={`${row.user_id}-${row.session_id}`}
              data-testid={`unusual-login-row-${row.user_id}`}
              className="flex items-center gap-3 p-2 bg-white border border-amber-200 rounded text-xs"
            >
              <span className="text-base leading-none flex-shrink-0">
                {flagEmoji(row.country_code) || '🌐'}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">
                  {row.name || row.email}
                </p>
                <p className="text-gray-500 flex items-center gap-2 flex-wrap">
                  <Globe size={10} className="text-amber-600 flex-shrink-0" />
                  <span className="font-medium text-amber-900">{row.country_name || row.country_code}</span>
                  <span className="text-gray-400">·</span>
                  <span>usually <span className="font-medium">{row.usual_country}</span></span>
                  <span className="text-gray-400">·</span>
                  <span className="font-mono">{row.ip}</span>
                  <span className="text-gray-400">·</span>
                  <span>{relTime(row.created_at)}</span>
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-amber-400 text-amber-800 hover:bg-amber-100 flex-shrink-0"
                onClick={() => onInspectUser?.(row.user_id)}
                data-testid={`unusual-login-inspect-${row.user_id}`}
              >
                Inspect
                <ChevronRight size={12} className="ml-1" />
              </Button>
            </div>
          ))}
          {rows.length > 5 && (
            <p className="text-[11px] text-amber-700/80 text-center pt-1">
              + {rows.length - 5} more — open Telemetry → Failed-Login Activity for the full list.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
